import React from 'react';
import { Box, Typography, Button } from '@mui/material';

/**
 * ApproveOrDeny - Confirmation dialog content offering approve, deny, or cancel
 *
 * @param {string} title - Optional heading (default: "Are you sure?")
 * @param {string} message - Optional message to display in the callout box
 * @param {string[]} bulletPoints - Optional list of items to display as bullet points
 * @param {string} errorMessage - Optional error to display below the callout box
 * @param {function} onApprove - Callback when approve button is clicked
 * @param {function} onDeny - Callback when deny button is clicked
 * @param {function} onCancel - Callback when cancel button is clicked
 * @param {string} approveButtonText - Optional custom text for approve button (default: "Approve")
 * @param {string} denyButtonText - Optional custom text for deny button (default: "Deny")
 * @param {string} cancelButtonText - Optional custom text for cancel button (default: "Cancel")
 * @param {boolean} disabled - Disables all three buttons while an action is in flight
 */
const ApproveOrDeny = ({
  title = 'Are you sure?',
  message,
  bulletPoints = [],
  errorMessage,
  onApprove,
  onDeny,
  onCancel,
  approveButtonText = 'Approve',
  denyButtonText = 'Deny',
  cancelButtonText = 'Cancel',
  disabled = false,
}) => {
  const hasCalloutContent = message || bulletPoints.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 3,
        }}
      >
        {title}
      </Typography>

      {/* Callout box with message and bullet points */}
      {hasCalloutContent && (
        <Box
          sx={{
            backgroundColor: '#F5F5F5',
            border: '2px solid #333333',
            borderRadius: 2,
            p: 2,
            mb: errorMessage ? 2 : 4,
          }}
        >
          {message && (
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: '0.95rem',
                mb: bulletPoints.length > 0 ? 1 : 0,
              }}
            >
              {message}
            </Typography>
          )}

          {bulletPoints.length > 0 && (
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 3,
                '& li': {
                  color: 'text.primary',
                  fontSize: '0.95rem',
                  py: 0.25,
                },
              }}
            >
              {bulletPoints.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Error message */}
      {errorMessage && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mb: 4, textAlign: 'center' }}
        >
          {errorMessage}
        </Typography>
      )}

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={onApprove}
          disabled={disabled}
          sx={{
            backgroundColor: '#2E7D32',
            color: '#ffffff',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#1B5E20',
            },
          }}
        >
          {approveButtonText}
        </Button>
        <Button
          variant="contained"
          onClick={onDeny}
          disabled={disabled}
          sx={{
            backgroundColor: '#8B1C1C',
            color: '#ffffff',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#6B1515',
            },
          }}
        >
          {denyButtonText}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={disabled}
          sx={{
            borderColor: '#333333',
            color: '#333333',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              borderColor: '#333333',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {cancelButtonText}
        </Button>
      </Box>
    </Box>
  );
};

export default ApproveOrDeny;
